// ===========================================
// Recent Resources Component
// ===========================================

import { Alert, Box, Button, List, Paper, Skeleton, Typography } from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  Folder as FolderIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { Resource, formatFileSize } from '../../../api/resources.api';

interface Props {
  resources: Resource[];
  isLoading: boolean;
  onViewAll: () => void;
}

/**
 * Recent Resources for Student Tab
 */
export default function RecentResources({ resources, isLoading, onViewAll }: Props) {
  if (isLoading) {
    return <Skeleton variant="rectangular" height={150} />;
  }

  const recentResources = resources
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 5);

  if (recentResources.length === 0) {
    return <Alert severity="info">No shared resources yet.</Alert>;
  }

  return (
    <Box>
      <List disablePadding>
        {recentResources.map((resource) => (
          <Paper key={resource.id} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FolderIcon color="primary" />
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {resource.fileName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {resource.Lesson?.name || 'General'} | {formatFileSize(resource.fileSize)}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {format(new Date(resource.createdAt), 'd MMM')}
              </Typography>
            </Box>
          </Paper>
        ))}
      </List>
      {resources.length > 5 && (
        <Button size="small" endIcon={<ArrowForwardIcon />} onClick={onViewAll}>
          View All Resources
        </Button>
      )}
    </Box>
  );
}
